import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import {
  getInteractionMatrix,
  getStatusBreakdown,
  getCompletionStats,
  getContentAnalytics,
  InteractionMatrix,
  StatusBreakdown,
  CompletionStats,
  ContentAnalytics
} from '../services/api';

const Analytics: React.FC = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [interactionMatrix, setInteractionMatrix] = useState<InteractionMatrix | null>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<StatusBreakdown | null>(null);
  const [completionStats, setCompletionStats] = useState<CompletionStats | null>(null);
  const [contentAnalytics, setContentAnalytics] = useState<ContentAnalytics | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);

    // Load each independently so one failure doesn't break the others
    try {
      const matrix = await getInteractionMatrix();
      setInteractionMatrix(matrix);
    } catch (error) {
      console.error('Error loading interaction matrix:', error);
    }

    try {
      const status = await getStatusBreakdown();
      setStatusBreakdown(status);
    } catch (error) {
      console.error('Error loading status breakdown:', error);
    }

    try {
      const stats = await getCompletionStats();
      setCompletionStats(stats);
    } catch (error) {
      console.error('Error loading completion stats:', error);
    }

    try {
      const content = await getContentAnalytics();
      setContentAnalytics(content);
    } catch (error) {
      console.error('Error loading content analytics:', error);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.background
      }}>
        <div style={{ color: theme.text }}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.background }}>
      {/* Action Bar */}
      <div style={{
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px', color: theme.text }}>Analytics</h1>
          <p style={{ margin: '4px 0 0 0', color: theme.textSecondary, fontSize: '13px' }}>
            Human-AI Interaction Statistics
          </p>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Summary Stats Cards */}
        {completionStats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '40px'
          }}>
            <StatCard
              title="Total Tasks"
              value={completionStats.total_tasks}
              theme={theme}
            />
            <StatCard
              title="Completed"
              value={completionStats.completed_tasks}
              subtitle={`${completionStats.completion_rate}%`}
              theme={theme}
              color="#10b981"
            />
            <StatCard
              title="Failed"
              value={completionStats.failed_tasks}
              subtitle={`${completionStats.failure_rate}%`}
              theme={theme}
              color="#ef4444"
            />
            <StatCard
              title="Avg. Completion"
              value={`${completionStats.avg_completion_minutes.toFixed(1)}m`}
              theme={theme}
            />
          </div>
        )}

        {/* Status Breakdown */}
        {statusBreakdown && (
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '40px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: theme.text, fontSize: '20px' }}>
              Task Status Breakdown
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px'
            }}>
              {Object.entries(statusBreakdown).map(([status, count]) => (
                <div key={status} style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: getStatusColor(status),
                    marginBottom: '8px'
                  }}>
                    {count}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: theme.textSecondary,
                    textTransform: 'capitalize'
                  }}>
                    {status.replace('_', ' ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Analytics */}
        {contentAnalytics && (
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '40px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: theme.text, fontSize: '20px' }}>
              Content Overview
            </h2>

            {/* Totals Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: theme.surfaceHover,
              borderRadius: '8px',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
                  {contentAnalytics.totals.features}
                </div>
                <div style={{ fontSize: '13px', color: theme.textSecondary }}>Features</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6' }}>
                  {contentAnalytics.totals.ideas}
                </div>
                <div style={{ fontSize: '13px', color: theme.textSecondary }}>Ideas</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {contentAnalytics.totals.tasks}
                </div>
                <div style={{ fontSize: '13px', color: theme.textSecondary }}>Tasks</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>
                  {contentAnalytics.totals.prompts}
                </div>
                <div style={{ fontSize: '13px', color: theme.textSecondary }}>Prompts</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
                  {contentAnalytics.totals.completed_tasks}
                </div>
                <div style={{ fontSize: '13px', color: theme.textSecondary }}>Completed</div>
              </div>
            </div>

            {/* By Category Table */}
            <h3 style={{ margin: '0 0 16px 0', color: theme.text, fontSize: '16px' }}>
              By Category
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: theme.surfaceHover }}>
                    <th style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${theme.border}`,
                      color: theme.text,
                      fontWeight: '600',
                    }}>
                      Category
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: '#10b981',
                      fontWeight: '600',
                    }}>
                      Features
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: '#8b5cf6',
                      fontWeight: '600',
                    }}>
                      Ideas
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: '#3b82f6',
                      fontWeight: '600',
                    }}>
                      Tasks
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: '#f59e0b',
                      fontWeight: '600',
                    }}>
                      Prompts
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: theme.text,
                      fontWeight: '600',
                    }}>
                      Completion
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(contentAnalytics.by_category)
                    .sort((a, b) => b[1].prompts - a[1].prompts)
                    .map(([catId, catData]) => {
                    const completionRate = catData.tasks > 0
                      ? Math.round((catData.completed_tasks / catData.tasks) * 100)
                      : 0;
                    return (
                      <tr key={catId}>
                        <td style={{
                          padding: '12px',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                          fontWeight: '500',
                        }}>
                          {catData.category_name}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                        }}>
                          {catData.features}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                        }}>
                          {catData.ideas}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                        }}>
                          {catData.tasks}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                        }}>
                          {catData.prompts}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${theme.border}`,
                        }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: completionRate >= 75 ? '#10b98120' : completionRate >= 50 ? '#f59e0b20' : '#ef444420',
                            color: completionRate >= 75 ? '#10b981' : completionRate >= 50 ? '#f59e0b' : '#ef4444',
                          }}>
                            {catData.completed_tasks}/{catData.tasks} ({completionRate}%)
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* By Human Table */}
            {contentAnalytics.by_human && Object.keys(contentAnalytics.by_human).length > 0 && (
              <>
                <h3 style={{ margin: '24px 0 16px 0', color: theme.text, fontSize: '16px' }}>
                  By Human
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: theme.surfaceHover }}>
                        <th style={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: `2px solid ${theme.border}`,
                          color: theme.text,
                          fontWeight: '600',
                        }}>
                          Human
                        </th>
                        <th style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `2px solid ${theme.border}`,
                          color: '#8b5cf6',
                          fontWeight: '600',
                        }}>
                          Ideas
                        </th>
                        <th style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `2px solid ${theme.border}`,
                          color: '#3b82f6',
                          fontWeight: '600',
                        }}>
                          Tasks
                        </th>
                        <th style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `2px solid ${theme.border}`,
                          color: '#f59e0b',
                          fontWeight: '600',
                        }}>
                          Prompts
                        </th>
                        <th style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: `2px solid ${theme.border}`,
                          color: theme.text,
                          fontWeight: '600',
                        }}>
                          Completion
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(contentAnalytics.by_human)
                        .sort((a, b) => b[1].prompts - a[1].prompts)
                        .map(([humanName, humanData]) => {
                        const completionRate = humanData.tasks > 0
                          ? Math.round((humanData.completed_tasks / humanData.tasks) * 100)
                          : 0;
                        return (
                          <tr key={humanName}>
                            <td style={{
                              padding: '12px',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.text,
                              fontWeight: '500',
                            }}>
                              {humanName}
                            </td>
                            <td style={{
                              padding: '12px',
                              textAlign: 'center',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.text,
                            }}>
                              {humanData.ideas}
                            </td>
                            <td style={{
                              padding: '12px',
                              textAlign: 'center',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.text,
                            }}>
                              {humanData.tasks}
                            </td>
                            <td style={{
                              padding: '12px',
                              textAlign: 'center',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.text,
                            }}>
                              {humanData.prompts}
                            </td>
                            <td style={{
                              padding: '12px',
                              textAlign: 'center',
                              borderBottom: `1px solid ${theme.border}`,
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: completionRate >= 75 ? '#10b98120' : completionRate >= 50 ? '#f59e0b20' : '#ef444420',
                                color: completionRate >= 75 ? '#10b981' : completionRate >= 50 ? '#f59e0b' : '#ef4444',
                              }}>
                                {humanData.completed_tasks}/{humanData.tasks} ({completionRate}%)
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Interaction Matrix */}
        {interactionMatrix && interactionMatrix.humans.length > 0 && (
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: theme.text, fontSize: '20px' }}>
              Human-AI Interaction Matrix
            </h2>
            <p style={{ color: theme.textSecondary, marginBottom: '20px', fontSize: '14px' }}>
              Number of tasks assigned by each human to each AI team member
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: theme.surfaceHover }}>
                    <th style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${theme.border}`,
                      color: theme.text,
                      fontWeight: '600',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: theme.surfaceHover,
                      zIndex: 1
                    }}>
                      Human / AI
                    </th>
                    {interactionMatrix.ai_members.map(ai => (
                      <th key={ai.name} style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: `2px solid ${theme.border}`,
                        color: theme.text,
                        fontWeight: '600',
                        minWidth: '100px'
                      }}>
                        <div>{ai.name}</div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 'normal',
                          color: theme.textSecondary,
                          marginTop: '4px'
                        }}>
                          {ai.role}
                        </div>
                      </th>
                    ))}
                    <th style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.border}`,
                      color: theme.text,
                      fontWeight: '600',
                      backgroundColor: '#f3f4f6',
                      minWidth: '80px'
                    }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...interactionMatrix.humans]
                    .sort((a, b) => (interactionMatrix.totals.by_human[b] || 0) - (interactionMatrix.totals.by_human[a] || 0))
                    .map(human => (
                    <tr key={human}>
                      <td style={{
                        padding: '12px',
                        borderBottom: `1px solid ${theme.border}`,
                        fontWeight: '600',
                        color: theme.text,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: theme.surface,
                        zIndex: 1
                      }}>
                        {human}
                      </td>
                      {interactionMatrix.ai_members.map(ai => {
                        const count = interactionMatrix.matrix[human]?.[ai.name] || 0;
                        return (
                          <td key={ai.name} style={{
                            padding: '12px',
                            textAlign: 'center',
                            borderBottom: `1px solid ${theme.border}`,
                            color: theme.text,
                            backgroundColor: count > 0 ? getCellColor(count) : 'transparent'
                          }}>
                            {count > 0 ? count : '-'}
                          </td>
                        );
                      })}
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${theme.border}`,
                        fontWeight: '600',
                        color: theme.text,
                        backgroundColor: '#f3f4f6'
                      }}>
                        {interactionMatrix.totals.by_human[human] || 0}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <td style={{
                      padding: '12px',
                      fontWeight: '600',
                      color: theme.text,
                      borderTop: `2px solid ${theme.border}`
                    }}>
                      Total
                    </td>
                    {interactionMatrix.ai_members.map(ai => (
                      <td key={ai.name} style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: theme.text,
                        borderTop: `2px solid ${theme.border}`
                      }}>
                        {interactionMatrix.totals.by_ai[ai.name] || 0}
                      </td>
                    ))}
                    <td style={{
                      padding: '12px',
                      textAlign: 'center',
                      fontWeight: '700',
                      fontSize: '16px',
                      color: theme.text,
                      borderTop: `2px solid ${theme.border}`
                    }}>
                      {interactionMatrix.totals.overall}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(!interactionMatrix || interactionMatrix.humans.length === 0) && (
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '12px',
            padding: '60px',
            textAlign: 'center',
            color: theme.textSecondary
          }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>No data available yet</p>
            <p style={{ fontSize: '14px' }}>Create some tasks to see analytics</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component for stat cards
const StatCard: React.FC<{
  title: string;
  value: number | string;
  subtitle?: string;
  theme: any;
  color?: string;
}> = ({ title, value, subtitle, theme, color }) => (
  <div style={{
    backgroundColor: theme.surface,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  }}>
    <div style={{
      fontSize: '14px',
      color: theme.textSecondary,
      marginBottom: '8px'
    }}>
      {title}
    </div>
    <div style={{
      fontSize: '32px',
      fontWeight: 'bold',
      color: color || theme.text,
      marginBottom: subtitle ? '4px' : '0'
    }}>
      {value}
    </div>
    {subtitle && (
      <div style={{
        fontSize: '14px',
        color: theme.textSecondary
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#10b981';
    case 'in_progress': return '#3b82f6';
    case 'queued': return '#f59e0b';
    case 'failed': return '#ef4444';
    default: return '#6b7280';
  }
};

// Helper function to get cell background color based on count
const getCellColor = (count: number): string => {
  if (count >= 10) return 'rgba(59, 130, 246, 0.3)';
  if (count >= 5) return 'rgba(59, 130, 246, 0.2)';
  if (count >= 2) return 'rgba(59, 130, 246, 0.1)';
  return 'rgba(59, 130, 246, 0.05)';
};

export default Analytics;
